import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Avatar,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Save as SaveIcon,
  CloudUpload as UploadIcon,
  CloudDownload as DownloadIcon,
  Storage as StorageIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpOutlineIcon,
  RestartAlt as RestartAltIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api, { exportApi, aziendeApi } from '../services/api';
import AziendeManager from '../components/AziendeManager';
import PayPalSettingsPanel from '../components/PayPalSettingsPanel';
import { formatNuf } from '../utils/formatNuf';

// Template email predefinito
const DEFAULT_EMAIL_SUBJECT = 'Fattura n. {{nuf}} del {{emission_date}} - {{company_name}}';
const DEFAULT_EMAIL_BODY = `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  {{company_logo}}
  
  <p>Gentile <strong>{{customer_name}}</strong>,</p>
  
  <p>in allegato trova la fattura <strong>n. {{nuf}}</strong> relativa al periodo <strong>{{period}}</strong>.</p>
  
  <table style="width: 100%; border-collapse: collapse; margin: 20px 0; background: #f9f9f9;">
    <tr>
      <td style="padding: 10px; border: 1px solid #ddd;"><strong>Numero Fattura:</strong></td>
      <td style="padding: 10px; border: 1px solid #ddd;">{{nuf}}</td>
    </tr>
    <tr>
      <td style="padding: 10px; border: 1px solid #ddd;"><strong>Data Emissione:</strong></td>
      <td style="padding: 10px; border: 1px solid #ddd;">{{emission_date}}</td>
    </tr>
    <tr>
      <td style="padding: 10px; border: 1px solid #ddd;"><strong>Scadenza:</strong></td>
      <td style="padding: 10px; border: 1px solid #ddd;">{{due_date}}</td>
    </tr>
    <tr>
      <td style="padding: 10px; border: 1px solid #ddd;"><strong>Importo Totale:</strong></td>
      <td style="padding: 10px; border: 1px solid #ddd; font-size: 1.2em; color: #2196F3;"><strong>{{total}}</strong></td>
    </tr>
  </table>
  
  <p>Il pagamento può essere effettuato tramite:</p>
  <ul>
    <li>Bollettino postale allegato</li>
    <li>Bonifico bancario (IBAN indicato in fattura)</li>
  </ul>
  
  <p>Per qualsiasi chiarimento, non esiti a contattarci.</p>
  
  <p>Cordiali saluti,<br/>
  <strong>{{company_name}}</strong></p>
  
  <hr style="border: none; border-top: 1px solid #ddd; margin: 20px 0;" />
  <p style="font-size: 0.85em; color: #666;">
    Questo messaggio è stato generato automaticamente. Si prega di non rispondere a questa email.
  </p>
</div>`;

const Settings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin';
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [importDialog, setImportDialog] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const numberingRequestRef = useRef(0);

  // Stato multi-azienda
  const [selectedAzienda, setSelectedAzienda] = useState(null);

  // Dati azienda
  const [companyData, setCompanyData] = useState({
    company_name: '',
    address: '',
    cap: '',
    city: '',
    province: '',
    vat_number: '',
    fiscal_code: '',
    phone: '',
    whatsapp: '',
    email: '',
    company_logo: null,
    // Coordinate bancarie
    bank_name: '',
    bank_beneficiary: '',
    bank_iban: '',
    bank_bic: '',
    // Bollettino TD123
    bollettino_beneficiary_name: '',
    bollettino_ccp: '',
    
    // SEPA Direct Debit
    sepa_cuc: '',
    sepa_creditor_id: '',
    sepa_sia: '',
    
    // Spese spedizione automatiche
    default_shipping_cost: '',

    // SMTP (Email)
    smtp_host: '',
    smtp_port: 587,
    smtp_secure: false,
    smtp_user: '',
    smtp_password: '',
    smtp_password_set: false,
    smtp_from_name: '',
    smtp_from_email: '',
    
    // Template email fatture
    invoice_email_subject: '',
    invoice_email_body: '',
    
    // Blacklist sincronizzazione email
    email_sync_blacklist: '',
    
    // Impostazioni solleciti pagamento
    reminder_days_threshold: 10,
    disconnection_days_threshold: 60,
    disconnection_notice_days: 15,
    reminder_fee: 1.80,
    reminder_email_subject: '',
    reminder_email_body: '',
    disconnection_email_subject: '',
    disconnection_email_body: '',
  });

  // Impostazioni fatturazione
  const [billingSettings, setBillingSettings] = useState({
    default_vat_rate: 22,
    default_due_days: 30,
    auto_numbering: true,
    auto_email: true,
  });
  
  const [currentNumbering, setCurrentNumbering] = useState(null);

  // Password change
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  // Export CSV
  const [exportEntity, setExportEntity] = useState('customers');
  const [exporting, setExporting] = useState(false);

  // Helper: mappa i dati azienda nello state companyData
  const mapToCompanyData = (data) => ({
    company_name: data.company_name || '',
    address: data.address || '',
    cap: data.cap || '',
    city: data.city || '',
    province: data.province || '',
    vat_number: data.vat_number || '',
    fiscal_code: data.fiscal_code || '',
    phone: data.phone || '',
    whatsapp: data.whatsapp || '',
    email: data.email || '',
    company_logo: data.company_logo || null,
    bank_name: data.bank_name || '',
    bank_beneficiary: data.bank_beneficiary || '',
    bank_iban: data.bank_iban || '',
    bank_bic: data.bank_bic || '',
    bollettino_beneficiary_name: data.bollettino_beneficiary_name || '',
    bollettino_ccp: data.bollettino_ccp || '',
    default_shipping_cost: data.default_shipping_cost ?? '',
    smtp_host: data.smtp_host || '',
    smtp_port: data.smtp_port ?? 587,
    smtp_secure: !!data.smtp_secure,
    smtp_user: data.smtp_user || '',
    smtp_password: '',
    smtp_password_set: !!data.smtp_password_set,
    smtp_from_name: data.smtp_from_name || '',
    smtp_from_email: data.smtp_from_email || '',
    invoice_email_subject: data.invoice_email_subject || '',
    invoice_email_body: data.invoice_email_body || '',
    email_sync_blacklist: data.email_sync_blacklist || '',
    reminder_days_threshold: data.reminder_days_threshold ?? 10,
    disconnection_days_threshold: data.disconnection_days_threshold ?? 60,
    disconnection_notice_days: data.disconnection_notice_days ?? 15,
    reminder_fee: data.reminder_fee ?? 1.80,
    reminder_email_subject: data.reminder_email_subject || '',
    reminder_email_body: data.reminder_email_body || '',
    disconnection_email_subject: data.disconnection_email_subject || '',
    disconnection_email_body: data.disconnection_email_body || '',
    last_invoice_number: data.last_invoice_number ?? '',
    sezionale: data.sezionale || '',
    sepa_cuc: data.sepa_cuc || '',
    sepa_creditor_id: data.sepa_creditor_id || '',
    sepa_sia: data.sepa_sia || '',
  });

  // Callback cambio azienda dal selettore
  const handleAziendaSelect = (azienda) => {
    setSelectedAzienda(azienda);
    if (azienda) {
      setCompanyData(mapToCompanyData(azienda));
    }
  };

  // Carica impostazioni salvate
  useEffect(() => {
    const savedBillingSettings = localStorage.getItem('billingSettings');
    
    if (savedBillingSettings) {
      setBillingSettings(JSON.parse(savedBillingSettings));
    }
  }, []);

  // Carica impostazioni azienda dal backend (solo se non c'è un'azienda selezionata dal Manager)
  useEffect(() => {
    let cancelled = false;

    const loadCompanySettings = async () => {
      // Se AziendeManager ha già selezionato un'azienda, non sovrascrivere
      if (selectedAzienda) return;
      
      try {
        const resp = await api.get('/company-settings');
        const data = resp?.data?.data;
        if (cancelled || !data) return;

        setCompanyData(mapToCompanyData(data));
        
      } catch (e) {
        // best-effort: se fallisce, restano i valori locali
      }
    };

    loadCompanySettings();
    return () => {
      cancelled = true;
    };
  }, [selectedAzienda]);

  const refreshInvoiceNumber = useCallback(async (aziendaId, sezionale) => {
    const requestId = ++numberingRequestRef.current;
    try {
      const response = await api.get('/invoices/last-number', {
        params: { ...(aziendaId ? { azienda_id: aziendaId } : {}), sezionale: sezionale || '' }
      });
      if (requestId === numberingRequestRef.current) {
        setCurrentNumbering(response?.data?.success ? response.data.data : null);
      }
    } catch (e) {
      if (requestId === numberingRequestRef.current) setCurrentNumbering(null);
    }
  }, []);

  useEffect(() => {
    setCurrentNumbering(null);
    refreshInvoiceNumber(selectedAzienda?.id, companyData.sezionale);
  }, [selectedAzienda?.id, companyData.sezionale, refreshInvoiceNumber]);

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      
      // Salva in localStorage solo billingSettings
      localStorage.setItem('billingSettings', JSON.stringify(billingSettings));

      // Salva tutti i dati azienda su backend (azienda specifica o company-settings legacy)
      try {
        const payload = {
          company_name: companyData.company_name,
          address: companyData.address,
          cap: companyData.cap,
          city: companyData.city,
          province: companyData.province,
          vat_number: companyData.vat_number,
          fiscal_code: companyData.fiscal_code,
          phone: companyData.phone,
          whatsapp: companyData.whatsapp,
          email: companyData.email,
          bank_name: companyData.bank_name,
          bank_beneficiary: companyData.bank_beneficiary,
          bank_iban: companyData.bank_iban,
          bank_bic: companyData.bank_bic,
          bollettino_beneficiary_name: companyData.bollettino_beneficiary_name,
          bollettino_ccp: companyData.bollettino_ccp,
          // Spese spedizione automatiche
          default_shipping_cost: companyData.default_shipping_cost || null,
        };

        if (isAdmin) {
          // SMTP
          payload.smtp_host = companyData.smtp_host;
          payload.smtp_port = companyData.smtp_port;
          payload.smtp_secure = companyData.smtp_secure;
          payload.smtp_user = companyData.smtp_user;
          payload.smtp_from_name = companyData.smtp_from_name;
          payload.smtp_from_email = companyData.smtp_from_email;

          // Password: inviala solo se compilata (altrimenti non cambia)
          if (companyData.smtp_password && companyData.smtp_password.trim()) {
            payload.smtp_password = companyData.smtp_password;
          }
          
          // Template email fatture
          payload.invoice_email_subject = companyData.invoice_email_subject;
          payload.invoice_email_body = companyData.invoice_email_body;
          
          // Blacklist sincronizzazione email (solo company-settings globale)
          if (!selectedAzienda) {
            payload.email_sync_blacklist = companyData.email_sync_blacklist;
          }
          
          // Impostazioni solleciti pagamento
          payload.reminder_days_threshold = companyData.reminder_days_threshold;
          payload.disconnection_days_threshold = companyData.disconnection_days_threshold;
          payload.disconnection_notice_days = companyData.disconnection_notice_days;
          payload.reminder_fee = companyData.reminder_fee;
          payload.reminder_email_subject = companyData.reminder_email_subject;
          payload.reminder_email_body = companyData.reminder_email_body;
          payload.disconnection_email_subject = companyData.disconnection_email_subject;
          payload.disconnection_email_body = companyData.disconnection_email_body;
          
          // Numero ultima fattura (gestisce anche 0 come valore valido)
          payload.last_invoice_number = companyData.last_invoice_number !== '' && companyData.last_invoice_number != null 
            ? companyData.last_invoice_number 
            : null;
          
          // Sezionale fatture
          payload.sezionale = companyData.sezionale || null;

          // SEPA Direct Debit
          payload.sepa_cuc = companyData.sepa_cuc || null;
          payload.sepa_creditor_id = companyData.sepa_creditor_id || null;
          payload.sepa_sia = companyData.sepa_sia || null;
        }

        let resp;
        if (selectedAzienda) {
          resp = await aziendeApi.update(selectedAzienda.id, payload);
        } else {
          resp = await api.put('/company-settings', payload);
        }

        // Dopo il salvataggio: pulisci il campo password e aggiorna lo stato "password impostata"
        const updated = resp?.data?.data;
        setCompanyData((prev) => ({
          ...prev,
          smtp_password: '',
          smtp_password_set: !!updated?.smtp_password_set || prev.smtp_password_set || !!(companyData.smtp_password && companyData.smtp_password.trim()),
        }));

        // Aggiorna anche l'oggetto selectedAzienda con i dati aggiornati
        if (selectedAzienda && updated) {
          setSelectedAzienda(updated);
        }
        await refreshInvoiceNumber(selectedAzienda?.id, companyData.sezionale);
      } catch (e) {
        setSnackbar({
          open: true,
          message: e?.response?.data?.message || 'Errore salvataggio impostazioni su server',
          severity: 'error',
        });
        return;
      }
      
      setSnackbar({
        open: true,
        message: 'Impostazioni salvate con successo',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Errore nel salvataggio delle impostazioni',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      setSnackbar({
        open: true,
        message: 'Le password non coincidono',
        severity: 'error',
      });
      return;
    }

    if (passwordData.new_password.length < 6) {
      setSnackbar({
        open: true,
        message: 'La password deve essere di almeno 6 caratteri',
        severity: 'error',
      });
      return;
    }

    try {
      setLoading(true);
      await api.post('/auth/change-password', {
        currentPassword: passwordData.current_password,
        newPassword: passwordData.new_password,
      });
      setSnackbar({
        open: true,
        message: 'Password modificata con successo',
        severity: 'success',
      });
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Errore nel cambio password',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      setLoading(true);
      
      // Simula export - in produzione sarebbe un API call
      const exportData = {
        companyData,
        billingSettings,
        exportDate: new Date().toISOString(),
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export_${Date.now()}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: 'Dati esportati con successo',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Errore nell\'esportazione dei dati',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackupDatabase = async () => {
    try {
      setLoading(true);
      
      // In produzione chiamerebbe l'API di backup
      // const response = await api.post('/admin/backup');
      
      setSnackbar({
        open: true,
        message: 'Backup database avviato. Riceverai una notifica al completamento.',
        severity: 'info',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Errore nel backup del database',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImportFoxPro = () => {
    setImportDialog(true);
  };

  // Export CSV
  const handleExportCSV = async () => {
    try {
      setExporting(true);
      
      let response;
      let filename;
      
      switch (exportEntity) {
        case 'customers':
          response = await exportApi.exportCustomers();
          filename = 'clienti';
          break;
        case 'invoices':
          response = await exportApi.exportInvoices();
          filename = 'fatture';
          break;
        case 'meters':
          response = await exportApi.exportMeters();
          filename = 'contatori';
          break;
        case 'plants':
          response = await exportApi.exportPlants();
          filename = 'impianti';
          break;
        case 'advances':
          response = await exportApi.exportAdvances();
          filename = 'acconti';
          break;
        case 'payments':
          response = await exportApi.exportPayments();
          filename = 'pagamenti';
          break;
        default:
          throw new Error('Entità non valida');
      }
      
      // Crea download
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: `${filename.charAt(0).toUpperCase() + filename.slice(1)} esportati con successo`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Errore export CSV:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Errore nell\'esportazione dei dati',
        severity: 'error',
      });
    } finally {
      setExporting(false);
    }
  };

  // Gestione logo
  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Verifica tipo file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setSnackbar({
        open: true,
        message: 'Tipo file non supportato. Usa JPG, PNG, GIF o WEBP.',
        severity: 'error',
      });
      return;
    }

    // Verifica dimensione (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({
        open: true,
        message: 'Il file è troppo grande. Massimo 5MB.',
        severity: 'error',
      });
      return;
    }

    try {
      setUploadingLogo(true);
      
      const formData = new FormData();
      formData.append('logo', file);
      
      let response;
      if (selectedAzienda) {
        response = await aziendeApi.uploadLogo(selectedAzienda.id, formData);
      } else {
        response = await api.post('/company-settings/logo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      if (response.data?.success) {
        setCompanyData(prev => ({
          ...prev,
          company_logo: response.data.data.company_logo
        }));
        setSnackbar({
          open: true,
          message: 'Logo caricato con successo',
          severity: 'success',
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Errore nel caricamento del logo',
        severity: 'error',
      });
    } finally {
      setUploadingLogo(false);
      // Reset input file
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    try {
      setUploadingLogo(true);
      
      if (selectedAzienda) {
        await aziendeApi.deleteLogo(selectedAzienda.id);
      } else {
        await api.delete('/company-settings/logo');
      }
      
      setCompanyData(prev => ({
        ...prev,
        company_logo: null
      }));
      
      setSnackbar({
        open: true,
        message: 'Logo rimosso con successo',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Errore nella rimozione del logo',
        severity: 'error',
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const getLogoUrl = () => {
    if (!companyData.company_logo) return null;
    const baseUrl = api.defaults.baseURL || '';
    if (selectedAzienda) {
      return `${baseUrl}/aziende/${selectedAzienda.id}/logo?t=${Date.now()}`;
    }
    return `${baseUrl}/company-settings/logo?t=${Date.now()}`;
  };

  const startImport = async () => {
    try {
      setImporting(true);
      setImportProgress(0);

      // Simula progresso importazione
      const tables = ['CLIENTI', 'CONTRATI', 'FATTURE', 'BOL', 'ACCONTI', 'SERBA'];
      for (let i = 0; i < tables.length; i++) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setImportProgress(((i + 1) / tables.length) * 100);
      }

      setSnackbar({
        open: true,
        message: 'Importazione completata con successo',
        severity: 'success',
      });
      setImportDialog(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Errore durante l\'importazione',
        severity: 'error',
      });
    } finally {
      setImporting(false);
      setImportProgress(0);
    }
  };

  const enteredLastNumber = companyData.last_invoice_number === '' || companyData.last_invoice_number == null
    ? null : Number(companyData.last_invoice_number);
  const invalidLastNumber = enteredLastNumber != null && (!Number.isSafeInteger(enteredLastNumber) || enteredLastNumber < 0);
  const manualNumberChanged = enteredLastNumber != null && enteredLastNumber !== currentNumbering?.manual_number;

  return (
    <Box sx={{ p: { xs: 0, sm: 0 } }}>
      <Typography variant={isMobile ? 'h6' : 'h4'} fontWeight="bold" sx={{ mb: 1 }}>
        Impostazioni
      </Typography>
      {!isMobile && <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configura le impostazioni dell'applicazione
      </Typography>}

      {/* Selettore multi-azienda */}
      <AziendeManager
        onSelect={handleAziendaSelect}
        selectedId={selectedAzienda?.id}
      />
      {selectedAzienda && <PayPalSettingsPanel key={selectedAzienda.id} aziendaId={selectedAzienda.id} companyName={selectedAzienda.company_name} />}

      <Grid container spacing={isMobile ? 1.5 : 3}>
        {/* Dati Azienda */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Dati Azienda
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {/* Logo Aziendale */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2,
                    p: 2,
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.default'
                  }}>
                    <Avatar
                      src={getLogoUrl()}
                      sx={{ 
                        width: 80, 
                        height: 80,
                        bgcolor: 'primary.light',
                        fontSize: '2rem'
                      }}
                    >
                      {companyData.company_name?.charAt(0)?.toUpperCase() || '?'}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Logo Aziendale
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        JPG, PNG, GIF o WEBP. Max 5MB.
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <input
                          ref={logoInputRef}
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          style={{ display: 'none' }}
                          id="logo-upload"
                          type="file"
                          onChange={handleLogoUpload}
                        />
                        <label htmlFor="logo-upload">
                          <Button
                            variant="outlined"
                            size="small"
                            component="span"
                            startIcon={uploadingLogo ? <CircularProgress size={16} /> : <PhotoCameraIcon />}
                            disabled={uploadingLogo || !isAdmin}
                          >
                            {companyData.company_logo ? 'Cambia' : 'Carica'}
                          </Button>
                        </label>
                        {companyData.company_logo && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={handleDeleteLogo}
                            disabled={uploadingLogo || !isAdmin}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Ragione Sociale"
                    fullWidth
                    value={companyData.company_name}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, company_name: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Indirizzo"
                    fullWidth
                    value={companyData.address}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, address: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="CAP"
                    fullWidth
                    value={companyData.cap}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, cap: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    label="Città"
                    fullWidth
                    value={companyData.city}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, city: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    label="Prov."
                    fullWidth
                    value={companyData.province}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, province: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Partita IVA"
                    fullWidth
                    value={companyData.vat_number}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, vat_number: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Codice Fiscale"
                    fullWidth
                    value={companyData.fiscal_code}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, fiscal_code: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Telefono"
                    fullWidth
                    value={companyData.phone}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, phone: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="WhatsApp"
                    fullWidth
                    placeholder="es. 3299028484"
                    value={companyData.whatsapp}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, whatsapp: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Email"
                    fullWidth
                    value={companyData.email}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, email: e.target.value })
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" fontWeight="bold">
                    Coordinate Bancarie
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Nome Banca"
                    fullWidth
                    value={companyData.bank_name}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, bank_name: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Intestatario Conto (Beneficiario)"
                    fullWidth
                    value={companyData.bank_beneficiary}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, bank_beneficiary: e.target.value })
                    }
                    helperText="Nome del beneficiario per bonifici bancari"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="IBAN"
                    fullWidth
                    value={companyData.bank_iban}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, bank_iban: e.target.value.toUpperCase() })
                    }
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="BIC/SWIFT"
                    fullWidth
                    value={companyData.bank_bic}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, bank_bic: e.target.value.toUpperCase() })
                    }
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" fontWeight="bold">
                    SEPA Direct Debit
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Codice CUC"
                    fullWidth
                    value={companyData.sepa_cuc}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, sepa_cuc: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8) })
                    }
                    inputProps={{ maxLength: 8, style: { textTransform: 'uppercase' } }}
                    helperText="Codice Univoco CBI (8 caratteri)"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Creditor Scheme ID"
                    fullWidth
                    value={companyData.sepa_creditor_id}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, sepa_creditor_id: e.target.value.toUpperCase() })
                    }
                    inputProps={{ maxLength: 35, style: { textTransform: 'uppercase' } }}
                    helperText="Es. IT31ZZZ0000000141230664"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Codice SIA"
                    fullWidth
                    value={companyData.sepa_sia}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, sepa_sia: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5) })
                    }
                    inputProps={{ maxLength: 5, style: { textTransform: 'uppercase' } }}
                    helperText="Codice SIA (5 caratteri, opzionale)"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" fontWeight="bold">
                    Bollettino TD123
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Beneficiario (Intestato a)"
                    fullWidth
                    value={companyData.bollettino_beneficiary_name}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, bollettino_beneficiary_name: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Conto Corrente Postale (C/C n.)"
                    fullWidth
                    value={companyData.bollettino_ccp}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, bollettino_ccp: e.target.value })
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" fontWeight="bold">
                    Fatturazione Automatica
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Spese Spedizione (€)"
                    fullWidth
                    type="number"
                    value={companyData.default_shipping_cost}
                    onChange={(e) =>
                      setCompanyData({ ...companyData, default_shipping_cost: e.target.value })
                    }
                    helperText="Applicato automaticamente ai clienti senza invio email"
                    InputProps={{
                      inputProps: { min: 0, step: 0.01 }
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Impostazioni Fatturazione */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Fatturazione
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Aliquota IVA Default"
                    fullWidth
                    type="number"
                    value={billingSettings.default_vat_rate}
                    onChange={(e) =>
                      setBillingSettings({
                        ...billingSettings,
                        default_vat_rate: parseFloat(e.target.value),
                      })
                    }
                    InputProps={{ endAdornment: '%' }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Giorni Scadenza Default"
                    fullWidth
                    type="number"
                    value={billingSettings.default_due_days}
                    onChange={(e) =>
                      setBillingSettings({
                        ...billingSettings,
                        default_due_days: parseInt(e.target.value),
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Sezionale Fatture"
                    fullWidth
                    value={companyData.sezionale || ''}
                    onChange={(e) => {
                      const val = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 10);
                      setCompanyData({
                        ...companyData,
                        sezionale: val,
                      });
                    }}
                    helperText={
                      companyData.sezionale
                        ? `Il numero fattura apparir\u00e0 come: ${companyData.sezionale}-00001, ${companyData.sezionale}-00002...`
                        : 'Inserisci un prefisso (es. GP, A, FE) per il sezionale fatture. Lascia vuoto per non usare sezionali.'
                    }
                    disabled={!isAdmin}
                    inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
                    placeholder="es. GP, A, FE"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Numero Ultima Fattura"
                    fullWidth
                    type="number"
                    value={companyData.last_invoice_number ?? ''}
                    onChange={(e) =>
                      setCompanyData({
                        ...companyData,
                        last_invoice_number: e.target.value,
                      })
                    }
                    error={invalidLastNumber}
                    helperText={invalidLastNumber
                      ? 'Inserisci un numero intero maggiore o uguale a 0.'
                      : manualNumberChanged
                        ? `Dopo il salvataggio si ripartirà dal primo numero libero da ${formatNuf(companyData.sezionale, enteredLastNumber + 1)}. Le fatture ancora presenti mantengono il loro numero.`
                      : currentNumbering
                        ? `Ultima fattura presente: ${currentNumbering.last_number}. Prossima fattura: ${formatNuf(companyData.sezionale, currentNumbering.next_number)}. Puoi impostare anche un numero più basso; quelli già occupati saranno saltati.`
                        : 'Lascia vuoto per continuare automaticamente.'}
                    disabled={!isAdmin}
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={billingSettings.auto_numbering}
                        onChange={(e) =>
                          setBillingSettings({
                            ...billingSettings,
                            auto_numbering: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Numerazione automatica fatture"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={billingSettings.auto_email}
                        onChange={(e) =>
                          setBillingSettings({
                            ...billingSettings,
                            auto_email: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Invia email automatica fattura"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* SMTP Email */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Email (SMTP)
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {!isAdmin && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Solo gli amministratori possono modificare la configurazione SMTP.
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="SMTP Host"
                    fullWidth
                    value={companyData.smtp_host}
                    disabled={!isAdmin}
                    onChange={(e) => setCompanyData({ ...companyData, smtp_host: e.target.value })}
                    placeholder="es. mail.tuodominio.it"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Porta"
                    fullWidth
                    type="number"
                    value={companyData.smtp_port}
                    disabled={!isAdmin}
                    onChange={(e) =>
                      setCompanyData({
                        ...companyData,
                        smtp_port: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                      })
                    }
                    placeholder="587"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!companyData.smtp_secure}
                        disabled={!isAdmin}
                        onChange={(e) => setCompanyData({ ...companyData, smtp_secure: e.target.checked })}
                      />
                    }
                    label="Connessione sicura (SSL/TLS)"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="SMTP Username"
                    fullWidth
                    value={companyData.smtp_user}
                    disabled={!isAdmin}
                    onChange={(e) => setCompanyData({ ...companyData, smtp_user: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="SMTP Password"
                    fullWidth
                    type="password"
                    value={companyData.smtp_password}
                    disabled={!isAdmin}
                    onChange={(e) => setCompanyData({ ...companyData, smtp_password: e.target.value })}
                    helperText={companyData.smtp_password_set ? 'Password impostata (lascia vuoto per non cambiarla)' : 'Non impostata (compila per salvarla)'}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" fontWeight="bold">
                    Mittente
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="From Name"
                    fullWidth
                    value={companyData.smtp_from_name}
                    disabled={!isAdmin}
                    onChange={(e) => setCompanyData({ ...companyData, smtp_from_name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="From Email"
                    fullWidth
                    value={companyData.smtp_from_email}
                    disabled={!isAdmin}
                    onChange={(e) => setCompanyData({ ...companyData, smtp_from_email: e.target.value })}
                    placeholder="es. amministrazione@tuodominio.it"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Template Email Fatture */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  Template Email Fatture
                </Typography>
                {isAdmin && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RestartAltIcon />}
                    onClick={() => {
                      setCompanyData({
                        ...companyData,
                        invoice_email_subject: DEFAULT_EMAIL_SUBJECT,
                        invoice_email_body: DEFAULT_EMAIL_BODY
                      });
                      setSnackbar({ open: true, message: 'Template predefinito ripristinato', severity: 'info' });
                    }}
                  >
                    Ripristina Predefinito
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />

              {!isAdmin && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Solo gli amministratori possono modificare il template email.
                </Alert>
              )}

              {/* Guida Template */}
              <Accordion sx={{ mb: 2, bgcolor: 'grey.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HelpOutlineIcon color="primary" fontSize="small" />
                    <Typography fontWeight="medium">Guida alla Personalizzazione del Template</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" paragraph>
                    Puoi personalizzare l'email che verrà inviata ai clienti con le fatture. 
                    Usa i <strong>placeholder</strong> (segnaposto) per inserire automaticamente i dati della fattura.
                  </Typography>
                  
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Placeholder Disponibili:
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell><strong>Placeholder</strong></TableCell>
                          <TableCell><strong>Descrizione</strong></TableCell>
                          <TableCell><strong>Esempio</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            <Chip label="{{nuf}}" size="small" variant="outlined" onClick={() => navigator.clipboard.writeText('{{nuf}}')} sx={{ cursor: 'pointer' }} />
                          </TableCell>
                          <TableCell>Numero fattura</TableCell>
                          <TableCell>1234</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Chip label="{{customer_name}}" size="small" variant="outlined" onClick={() => navigator.clipboard.writeText('{{customer_name}}')} sx={{ cursor: 'pointer' }} />
                          </TableCell>
                          <TableCell>Nome completo del cliente</TableCell>
                          <TableCell>Mario Rossi</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Chip label="{{emission_date}}" size="small" variant="outlined" onClick={() => navigator.clipboard.writeText('{{emission_date}}')} sx={{ cursor: 'pointer' }} />
                          </TableCell>
                          <TableCell>Data di emissione fattura</TableCell>
                          <TableCell>15/01/2026</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Chip label="{{due_date}}" size="small" variant="outlined" onClick={() => navigator.clipboard.writeText('{{due_date}}')} sx={{ cursor: 'pointer' }} />
                          </TableCell>
                          <TableCell>Data di scadenza pagamento</TableCell>
                          <TableCell>15/02/2026</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Chip label="{{total}}" size="small" variant="outlined" onClick={() => navigator.clipboard.writeText('{{total}}')} sx={{ cursor: 'pointer' }} />
                          </TableCell>
                          <TableCell>Importo totale formattato</TableCell>
                          <TableCell>€ 125,50</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Chip label="{{period}}" size="small" variant="outlined" onClick={() => navigator.clipboard.writeText('{{period}}')} sx={{ cursor: 'pointer' }} />
                          </TableCell>
                          <TableCell>Periodo di riferimento</TableCell>
                          <TableCell>Gen-Feb 2026</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Chip label="{{company_name}}" size="small" variant="outlined" onClick={() => navigator.clipboard.writeText('{{company_name}}')} sx={{ cursor: 'pointer' }} />
                          </TableCell>
                          <TableCell>Nome della tua azienda</TableCell>
                          <TableCell>GPL Service Srl</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <Chip label="{{company_logo}}" size="small" variant="outlined" onClick={() => navigator.clipboard.writeText('{{company_logo}}')} sx={{ cursor: 'pointer' }} />
                          </TableCell>
                          <TableCell>Logo aziendale (immagine)</TableCell>
                          <TableCell><em>Viene inserito automaticamente se caricato</em></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Suggerimento:</strong> Clicca su un placeholder per copiarlo negli appunti, poi incollalo nel template.
                    </Typography>
                  </Alert>

                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Formattazione HTML:
                  </Typography>
                  <Typography variant="body2" component="div">
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li><code>&lt;p&gt;testo&lt;/p&gt;</code> - Paragrafo</li>
                      <li><code>&lt;strong&gt;testo&lt;/strong&gt;</code> - Grassetto</li>
                      <li><code>&lt;em&gt;testo&lt;/em&gt;</code> - Corsivo</li>
                      <li><code>&lt;br/&gt;</code> - A capo</li>
                      <li><code>&lt;ul&gt;&lt;li&gt;elemento&lt;/li&gt;&lt;/ul&gt;</code> - Lista puntata</li>
                      <li><code>&lt;a href="url"&gt;link&lt;/a&gt;</code> - Link cliccabile</li>
                    </ul>
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Oggetto Email"
                    fullWidth
                    value={companyData.invoice_email_subject || ''}
                    disabled={!isAdmin}
                    onChange={(e) => setCompanyData({ ...companyData, invoice_email_subject: e.target.value })}
                    placeholder={DEFAULT_EMAIL_SUBJECT}
                    helperText="Lascia vuoto per usare il template predefinito"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Corpo Email (HTML)"
                    fullWidth
                    multiline
                    rows={12}
                    value={companyData.invoice_email_body || ''}
                    disabled={!isAdmin}
                    onChange={(e) => setCompanyData({ ...companyData, invoice_email_body: e.target.value })}
                    placeholder={DEFAULT_EMAIL_BODY}
                    helperText="Lascia vuoto per usare il template predefinito. Puoi usare HTML per la formattazione."
                    InputLabelProps={{ shrink: true }}
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        fontFamily: 'monospace',
                        fontSize: '0.85rem'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info" icon={<ContentCopyIcon />}>
                    <Typography variant="body2">
                      <strong>Nota:</strong> L'email includerà automaticamente in allegato il PDF della fattura con bollettino postale.
                      Il template predefinito è professionale e già ottimizzato per la maggior parte dei casi.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Impostazioni Solleciti Pagamento */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                  ⚠️ Impostazioni Solleciti Pagamento
                </Typography>
              </Box>

              {!isAdmin && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Solo gli amministratori possono modificare le impostazioni dei solleciti.
                </Alert>
              )}

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Soglie Temporali
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Giorni per Promemoria"
                    type="number"
                    fullWidth
                    value={companyData.reminder_days_threshold || 10}
                    disabled={!isAdmin}
                    onChange={(e) => setCompanyData({ ...companyData, reminder_days_threshold: parseInt(e.target.value) || 10 })}
                    helperText="Fatture scadute da più di X giorni"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Giorni per Sollecito Distacco"
                    type="number"
                    fullWidth
                    value={companyData.disconnection_days_threshold || 60}
                    disabled={!isAdmin}
                    onChange={(e) => setCompanyData({ ...companyData, disconnection_days_threshold: parseInt(e.target.value) || 60 })}
                    helperText="Fatture scadute da più di X giorni per sollecito grave"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Giorni Preavviso Distacco"
                    type="number"
                    fullWidth
                    value={companyData.disconnection_notice_days || 15}
                    disabled={!isAdmin}
                    onChange={(e) => setCompanyData({ ...companyData, disconnection_notice_days: parseInt(e.target.value) || 15 })}
                    helperText="Termine concesso per il pagamento prima del distacco"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Spese di Sollecito (€)"
                    type="number"
                    fullWidth
                    value={companyData.reminder_fee ?? 1.80}
                    disabled={!isAdmin}
                    onChange={(e) => { const v = parseFloat(e.target.value); setCompanyData({ ...companyData, reminder_fee: Number.isFinite(v) ? v : 0 }); }}
                    helperText="Spese addebitate per ogni sollecito inviato"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 0, step: 0.10 }}
                  />
                </Grid>
              </Grid>

              <Accordion sx={{ mb: 2, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" color="primary">
                    📌 Segnaposto disponibili per i template solleciti
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" component="div">
                    <strong>Dati Cliente:</strong>
                    <ul>
                      <li><code>{'{cliente}'}</code> - Nome del cliente</li>
                      <li><code>{'{email}'}</code> - Email del cliente</li>
                    </ul>
                    <strong>Dati Fatture:</strong>
                    <ul>
                      <li><code>{'{tabella_fatture}'}</code> - Tabella HTML con elenco fatture insolute</li>
                      <li><code>{'{totale}'}</code> - Totale importo dovuto (residuo + mora + spese)</li>
                      <li><code>{'{subtotale_fatture}'}</code> - Subtotale residuo fatture</li>
                      <li><code>{'{totale_mora}'}</code> - Totale interessi di mora</li>
                      <li><code>{'{spese_sollecito}'}</code> - Spese di sollecito</li>
                      <li><code>{'{num_fatture}'}</code> - Numero di fatture insolute</li>
                    </ul>
                    <strong>Dati Pagamento:</strong>
                    <ul>
                      <li><code>{'{info_pagamento}'}</code> - Blocco HTML con IBAN e C/C postale</li>
                      <li><code>{'{iban}'}</code> - Codice IBAN</li>
                      <li><code>{'{ccp}'}</code> - Conto Corrente Postale</li>
                    </ul>
                    <strong>Solo per Sollecito Distacco:</strong>
                    <ul>
                      <li><code>{'{data_scadenza}'}</code> - Data limite per il pagamento</li>
                      <li><code>{'{giorni_preavviso}'}</code> - Giorni concessi per il pagamento</li>
                    </ul>
                    <strong>Dati Azienda:</strong>
                    <ul>
                      <li><code>{'{azienda}'}</code> - Nome azienda</li>
                      <li><code>{'{telefono}'}</code> - Telefono azienda</li>
                      <li><code>{'{email_azienda}'}</code> - Email azienda</li>
                    </ul>
                  </Typography>
                </AccordionDetails>
              </Accordion>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Template Email Promemoria Pagamento
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <TextField
                    label="Oggetto Email Promemoria"
                    fullWidth
                    value={companyData.reminder_email_subject || ''}
                    disabled={!isAdmin}
                    onChange={(e) => setCompanyData({ ...companyData, reminder_email_subject: e.target.value })}
                    placeholder="Promemoria pagamento fatture - {azienda}"
                    helperText="Lascia vuoto per usare il template predefinito"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Corpo Email Promemoria (HTML)"
                    fullWidth
                    multiline
                    rows={8}
                    value={companyData.reminder_email_body || ''}
                    disabled={!isAdmin}
                    onChange={(e) => setCompanyData({ ...companyData, reminder_email_body: e.target.value })}
                    placeholder="Gentile {cliente},&#10;&#10;le ricordiamo che risultano non saldate le seguenti fatture:&#10;&#10;{tabella_fatture}&#10;&#10;Totale dovuto: € {totale}&#10;&#10;{info_pagamento}&#10;&#10;Cordiali saluti,&#10;{azienda}"
                    helperText="Lascia vuoto per usare il template predefinito"
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                  />
                </Grid>
              </Grid>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Template Email Sollecito con Intimazione Distacco
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Oggetto Email Sollecito Distacco"
                    fullWidth
                    value={companyData.disconnection_email_subject || ''}
                    disabled={!isAdmin}
                    onChange={(e) => setCompanyData({ ...companyData, disconnection_email_subject: e.target.value })}
                    placeholder="URGENTE: Sollecito pagamento con intimazione distacco - {azienda}"
                    helperText="Lascia vuoto per usare il template predefinito"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Corpo Email Sollecito Distacco (HTML)"
                    fullWidth
                    multiline
                    rows={10}
                    value={companyData.disconnection_email_body || ''}
                    disabled={!isAdmin}
                    onChange={(e) => setCompanyData({ ...companyData, disconnection_email_body: e.target.value })}
                    placeholder="Gentile {cliente},&#10;&#10;NONOSTANTE I PRECEDENTI SOLLECITI, risultano ancora non saldate le seguenti fatture:&#10;&#10;{tabella_fatture}&#10;&#10;Totale dovuto: € {totale}&#10;&#10;La informiamo che, in assenza di pagamento entro il {data_scadenza}, procederemo con la SOSPENSIONE della fornitura.&#10;&#10;{info_pagamento}&#10;&#10;Per qualsiasi chiarimento contattare: {telefono}&#10;&#10;Cordiali saluti,&#10;{azienda}"
                    helperText="Lascia vuoto per usare il template predefinito"
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="warning">
                    <Typography variant="body2">
                      <strong>Nota:</strong> Il sollecito con intimazione di distacco è una comunicazione formale. 
                      Assicurati che il testo sia conforme alle normative vigenti e alle condizioni contrattuali.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Blacklist Sincronizzazione Email */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                🚫 Blacklist Sincronizzazione Email
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Escludi indirizzi email dalla sincronizzazione IMAP (uno per riga). Le email da/verso questi indirizzi non verranno scaricate.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Email da Escludere"
                    fullWidth
                    multiline
                    rows={8}
                    value={companyData.email_sync_blacklist || ''}
                    disabled={!isAdmin}
                    onChange={(e) => setCompanyData({ ...companyData, email_sync_blacklist: e.target.value })}
                    placeholder="esempio@spam.com&#10;noreply@notifications.com&#10;newsletter@marketing.com&#10;# Puoi aggiungere commenti con #"
                    helperText="Un indirizzo email per riga. Puoi usare # per commenti. Le email verranno confrontate in modo case-insensitive."
                    InputLabelProps={{ shrink: true }}
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        fontFamily: 'monospace',
                        fontSize: '0.9rem'
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="warning">
                    <Typography variant="body2">
                      <strong>Attenzione:</strong> Le email già sincronizzate NON verranno eliminate. 
                      La blacklist si applica solo alle nuove sincronizzazioni. 
                      Il sistema sincronizza le email degli ultimi 3 anni.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Backup */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Backup e Dati
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<DownloadIcon />}
                  onClick={handleExportData}
                  disabled={loading}
                >
                  Esporta Dati (JSON)
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<StorageIcon />}
                  onClick={handleBackupDatabase}
                  disabled={loading}
                >
                  Backup Database
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  fullWidth
                  startIcon={<UploadIcon />}
                  onClick={handleImportFoxPro}
                  disabled={loading}
                >
                  Importa Dati FoxPro
                </Button>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                Per importare i dati da FoxPro, assicurati che i file .DBF siano
                nella cartella <code>data/foxpro</code> del server.
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Esportazione CSV */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Esportazione Dati CSV
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Esporta i dati in formato CSV compatibile con l'importazione.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo di dati</InputLabel>
                  <Select
                    value={exportEntity}
                    label="Tipo di dati"
                    onChange={(e) => setExportEntity(e.target.value)}
                    disabled={exporting}
                  >
                    <MenuItem value="customers">Clienti</MenuItem>
                    <MenuItem value="invoices">Fatture</MenuItem>
                    <MenuItem value="meters">Contatori</MenuItem>
                    <MenuItem value="plants">Impianti</MenuItem>
                    <MenuItem value="advances">Acconti</MenuItem>
                    <MenuItem value="payments">Pagamenti</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                  onClick={handleExportCSV}
                  disabled={exporting}
                  sx={{ minWidth: 140 }}
                >
                  {exporting ? 'Esporto...' : 'Esporta CSV'}
                </Button>
              </Box>

              <Alert severity="success" sx={{ mt: 2 }}>
                I file CSV esportati possono essere reimportati tramite la pagina Importazione.
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Account */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Account
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Username"
                    fullWidth
                    disabled
                    value={user?.username || 'admin'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Password Attuale"
                    type="password"
                    fullWidth
                    value={passwordData.current_password}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, current_password: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Nuova Password"
                    type="password"
                    fullWidth
                    value={passwordData.new_password}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, new_password: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Conferma Password"
                    type="password"
                    fullWidth
                    value={passwordData.confirm_password}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, confirm_password: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    onClick={handleChangePassword}
                    disabled={
                      !passwordData.current_password ||
                      !passwordData.new_password ||
                      !passwordData.confirm_password ||
                      loading
                    }
                  >
                    Cambia Password
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSaveSettings}
          disabled={loading}
        >
          Salva Impostazioni
        </Button>
      </Box>

      {/* Dialog Importazione FoxPro */}
      <Dialog open={importDialog} onClose={() => !importing && setImportDialog(false)} fullScreen={isMobile}>
        <DialogTitle>Importa Dati FoxPro</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Attenzione:</strong> Questa operazione importerà i dati dai file
            .DBF di FoxPro. I dati esistenti potrebbero essere sovrascritti.
          </Alert>

          <Typography variant="body2" sx={{ mb: 2 }}>
            Tabelle che verranno importate:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <li>CLIENTI.DBF → Anagrafica clienti</li>
            <li>CONTRATI.DBF → Contratti</li>
            <li>FATTURE.DBF → Fatture</li>
            <li>BOL.DBF → Bollette consegna</li>
            <li>ACCONTI.DBF → Acconti e pagamenti</li>
            <li>SERBA.DBF → Serbatoi</li>
          </Box>

          {importing && (
            <Box sx={{ width: '100%' }}>
              <LinearProgress variant="determinate" value={importProgress} />
              <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                {Math.round(importProgress)}% completato
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog(false)} disabled={importing}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={startImport}
            disabled={importing}
            startIcon={importing ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {importing ? 'Importazione...' : 'Avvia Importazione'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
